import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";

/**
 * BL-175 / D-266 (S170) — signal's GOLD signature "ORACLE · Command Aura".
 *
 * A GOLD signal tower (`tower.isGold()` + `special.goldAuraRadius/Damage`) becomes
 * a FORCE-MULTIPLIER, a brand-new EMPOWER (it buffs ALLIES, not enemies):
 *   • every FRIENDLY tower within `goldAuraRadius` px deals `goldAuraDamageMult`×
 *     damage (stamped as the per-frame transient `tower.commandAuraMult`, read at
 *     each tower's damage chokepoint);
 *   • all stealth on the board is REVEALED (the ORACLE sees everything).
 *
 * Recomputed every frame (reset → multiply) BEFORE the behaviors fire, so a buffed
 * tower's shots this frame already carry the buff. Wholly deterministic (a radius
 * scan, no `rng()`). With NO GOLD signal tower every `commandAuraMult` resets to 1
 * and no stealth is force-revealed → no surface in any fixture, byte-identical.
 *
 * The buff does NOT stack a GOLD signal tower onto itself (a signal tower deals no
 * damage anyway) but DOES stack across multiple GOLD ORACLE towers (the price
 * already makes that a rare, deliberate investment — the no-cap design).
 */
export function applyCommandAura(
  towers: Tower[],
  rangeMultiplier: number,
  enemies: Enemy[],
): void {
  // Fast reset every frame (the byte-identical default with no ORACLE present).
  let anyOracle = false;
  for (const t of towers) {
    t.commandAuraMult = 1;
    t.commandAuraLit = false;
    if (
      t.isGold() &&
      t.element === "signal" &&
      t.special?.goldAuraRadius !== undefined &&
      t.special.goldAuraDamageMult !== undefined
    ) {
      anyOracle = true;
    }
  }
  if (!anyOracle) return;

  for (const oracle of towers) {
    if (
      !oracle.isGold() ||
      oracle.element !== "signal" ||
      oracle.special?.goldAuraRadius === undefined ||
      oracle.special.goldAuraDamageMult === undefined
    ) {
      continue;
    }
    const radius = oracle.special.goldAuraRadius * rangeMultiplier;
    const rSq = radius * radius;
    const mult = oracle.special.goldAuraDamageMult;
    for (const tower of towers) {
      if (tower === oracle) continue;
      const dx = tower.x - oracle.x;
      const dy = tower.y - oracle.y;
      if (dx * dx + dy * dy > rSq) continue;
      tower.commandAuraMult *= mult;
      tower.commandAuraLit = true;
    }
    // ORACLE reveals all stealth on the board (range-independent — the all-seeing
    // command tower). Render-only flag handled by the beam reveal pass; here we
    // force the sim-visible `revealed` so targeting can engage hidden foes.
    for (const enemy of enemies) {
      if (enemy.active && enemy.stealth) enemy.revealed = true;
    }
  }
}
